import classnames from 'classnames';
import type { FC } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { objectKeyRemove } from '../../../../graphdebate/src/utils/objects';
import type { ModalInstance } from '../../components/GraphModal/Modal';
import { Modal } from '../../components/GraphModal/Modal';
import { failureToast } from '../../components/Toast';
import { frontConfig } from '../../config';
import type { GraphEditorInstance } from '../../editor/editor-model';
import { startOpenEditorStartFocusWorkaround } from '../../editor/editorjs/editorjs-utils';
import { useAppApolloClient } from '../../hooks/useAppApolloClient';
import { useConnectedRouter } from '../../hooks/useConnectedRouter';
import { useManualHistory } from '../../hooks/useManualHistory';
import usePrevious from '../../hooks/usePrevious';
import { ReactComponent as SendIconOutlined } from '../../icons/icon-send-outlined.svg';
import {
  useCurrentScrollSection,
  useCurrentScrollSectionForced,
  useScrollSectionsHelpers,
} from '../../services/scrollSections';
import type { Author } from '../../types/api';
import { handleError } from '../../utils/common-utils';
import { scrollToAnchor } from '../../utils/dom';
import type { CreateOrUpdateMessageVariablesSmall } from '../api/message';
import { aboveMessagesAnchor } from '../thread-utils';
import {
  submitAnswer,
  useNavToAnswer,
  useSortMessagesByNewest,
} from '../ThreadMessage/thread-service';
import { type EditorCloseCallback } from '../Write/ThreadEditorForm';
import { FooterAuthorWithAvatar } from './FooterAuthorWithAvatar';
import { FooterModalContent } from './FooterModalContent';
import { threadFooterMessages } from './messages';

/**
 * On mobile, the editor is not rendered with the bubbleflow, as it is on desktop. Instead, a fixed footer is displayed. While it could have been possible to use portals to keep the entry point in the bubbleflow, I'm not sure it would be simpler to implement. (Btw, it was implemented with a portal in the past, and was changed to simplify a couple of things).
 * When clicking buttons like new message, edit message... the request goes to this component through a function in a context. It updates the local states here to reveal the mobile editor modal.
 * Note: on mobile, there is an extra notion of "selected" messages, which is the message visible in the screen and is rendered with a shadow. When clicking the footer button, it opens an editor to answer the selected message. On desktop, there is no such selected message (not required, because we don't have a footer bar).
 */

//
// TODO move to ThreadMessage folder
//

const styledSpan = (chunk: React.ReactNode) => (
  <span className="text-gray-soft font-normal leading-[13px]">{chunk}</span>
);
const divCenter = (chunk: React.ReactNode) => <div className="flex items-center">{chunk}</div>;

const ThreadFooterAuthoringDetails = ({ author }: { author: Author }) => {
  const authorWithAvatar = useMemo(
    () => author && <FooterAuthorWithAvatar author={author} />,
    [author],
  );

  return (
    <div className="ThreadFooterAuthoringDetails flex gap-x-[9px] justify-start items-center truncate min-w-0 ml-[5px] text-[15px]">
      <FormattedMessage
        {...threadFooterMessages.replyToThreadAuthor}
        values={{
          authorWithAvatar,
          styledSpan,
          divCenter,
        }}
      />
    </div>
  );
};

export interface ThreadFooterEditorProps {
  className?: string;
  slug: string | undefined;
  topicId: string;
}

export const ThreadFooterEditor: FC<ThreadFooterEditorProps> = memo(function ThreadFooterEditor(
  props,
) {
  const { className, slug, topicId } = props;
  const { formatMessage } = useIntl();
  const { replace, query } = useConnectedRouter();

  const mobileAnswerEditorRef = useRef<GraphEditorInstance>(null);
  const modalRef = useRef<ModalInstance>(null);

  let [isAnswering, setIsAnswering] = useState(false);

  const currentScrollSectionForced = useCurrentScrollSectionForced();

  if (!isAnswering && currentScrollSectionForced) {
    isAnswering = true;
  }

  // When the user clicks "Answer" on a message, it sets a forced section. When a forced section has just been set, we show the modale.
  const prevForced = usePrevious(currentScrollSectionForced);
  useEffect(() => {
    if (
      currentScrollSectionForced &&
      currentScrollSectionForced !== prevForced &&
      modalRef.current
    ) {
      modalRef.current.show();
    }
  }, [currentScrollSectionForced, prevForced]);

  const currentScrollSection = useCurrentScrollSection();
  const currentScrollSectionRef = useRef(currentScrollSection);
  currentScrollSectionRef.current = currentScrollSection;
  const { unforceCurrentScrollSection } = useScrollSectionsHelpers();

  const navToAnswer = useNavToAnswer();

  const openEditorNoHistory = useCallback(() => {
    const id = currentScrollSectionRef.current?.messageId || currentScrollSectionRef.current?.id;
    if (!id) {
      const err = new Error('No message ID when opening the editor');
      handleError(err);
      if (frontConfig.IS_DEV) {
        throw err;
      } else {
        failureToast(
          "We couldn't open the editor, something went wrong. Please let us know so that we can fix it.",
        );
        return;
      }
    }
    if (currentScrollSectionRef.current?.noBubbleFlow) {
      navToAnswer(id);
      return;
    }
    // Used to be `setIsAnswering(true)` to not force the editor. But if the page scrolls behind the editor (it happens with Safari), it changes the current message, which changes the already opened editor, which is very bad.
    // forceCurrentScrollSection(id);
    // ^ Conflicts with a similar call when clicking the edit or answer button, that causes the edit to become an answer.
    // Let's keep the code a couple of weeks to ensure there is no hidden use case that requires it.
  }, [navToAnswer]);
  const closeEditorNoHistory = useCallback(() => {
    unforceCurrentScrollSection();
    setIsAnswering(false);
  }, [unforceCurrentScrollSection]);

  const { push: startAnswering, pop: stopAnswering } = useManualHistory(
    openEditorNoHistory,
    closeEditorNoHistory,
  );

  const handleCloseAndRemoveAnswerQuery = useCallback(() => {
    stopAnswering();
    replace({ query: objectKeyRemove(query, 'answer') }, undefined, { shallow: true });
  }, [stopAnswering, replace, query]);
  // usePopoverOverlayContext(isMobile() && isAnswering, stopAnswering);

  const isRoot = !currentScrollSection?.messageId;

  const serializeId = isRoot
    ? `new-root-answer-to-topic-${topicId}`
    : `new-answer-to-message-${currentScrollSection?.messageId}`;

  // const [initialValueRefAnswer, serializeValueAnswer, clearValueAnswer] =
  //   useSerializeValue(serializeId);

  const client = useAppApolloClient();

  //pagination settings
  const sortMessagesByNewest = useSortMessagesByNewest(slug);

  const handleSubmitAnswer = useCallback(
    async (variables: CreateOrUpdateMessageVariablesSmall) => {
      const isEditing = currentScrollSectionForced && currentScrollSectionForced !== true;
      /* const result = */ await submitAnswer(
        slug,
        currentScrollSection?.messageId,
        client,
        variables,
        !isEditing,
      );

      scrollToAnchor({ anchorName: aboveMessagesAnchor, options: { behavior: 'smooth' } });

      stopAnswering();
      // Open the vote modale
      currentScrollSection?.voteRef.current?.voteOnAnswer?.(currentScrollSection?.author);

      // return result;
      if (isRoot) {
        sortMessagesByNewest();
      }
    },
    [
      client,
      currentScrollSection?.author,
      currentScrollSection?.messageId,
      currentScrollSection?.voteRef,
      currentScrollSectionForced,
      isRoot,
      slug,
      sortMessagesByNewest,
      stopAnswering,
    ],
  );

  const stopAnsweringAndClear: EditorCloseCallback = useCallback(() => {
    stopAnswering();
    currentScrollSection?.nestedAnswersRef?.current?.closeEditorCancel();
  }, [currentScrollSection?.nestedAnswersRef, stopAnswering]);

  const renderModalContent = useCallback(
    (instance: ModalInstance) => (
      <FooterModalContent
        modal={instance}
        author={currentScrollSection?.author}
        serializeId={serializeId}
        mobileAnswerEditorRef={mobileAnswerEditorRef}
        onSubmitAnswer={handleSubmitAnswer}
        currentScrollSectionForced={currentScrollSectionForced}
        stopAnsweringAndClear={stopAnsweringAndClear}
      />
    ),
    [
      currentScrollSection?.author,
      currentScrollSectionForced,
      handleSubmitAnswer,
      serializeId,
      stopAnsweringAndClear,
    ],
  );

  const openAnswerBtn = (
    <button
      className={classnames(
        'flex flex-1 text-left items-center border border-solid border-gray-210',
        /* !isAnswering && */ 'rounded-full min-w-0',
      )}
      tabIndex={-1}
      onClick={currentScrollSectionRef.current?.noBubbleFlow ? startAnswering : undefined}
    >
      <div className="flex flex-1 min-w-0 text-[13px] text-gray-soft pl-5 py-3">
        {currentScrollSection?.author ? (
          <ThreadFooterAuthoringDetails author={currentScrollSection?.author} />
        ) : (
          // TODO fallback not required? TBC for the rare cases when the author is not available
          <FormattedMessage {...threadFooterMessages.answer} />
        )}
      </div>
      {/* !isAnswering && */ <SendIconOutlined className="text-[#5A5A5A] h-10 w-10" />}
    </button>
  );

  return (
    <div
      className={classnames(
        'ThreadMessageFooterEditor fixed bottom-0 left-0 right-0 z-mobileEditor min-h-12 pb-8 px-3 flex flex-col bg-white drop-shadow',
        className,
        /* !isAnswering && */ 'pt-3',
      )}
    >
      <div className={classnames('flex space-x-2 min-w-0')}>
        {currentScrollSectionRef.current?.noBubbleFlow ? (
          openAnswerBtn
        ) : (
          <Modal
            ref={modalRef}
            lazyContent={renderModalContent}
            fullScreen
            onShow={startAnswering}
            onHide={handleCloseAndRemoveAnswerQuery} // avoid to re-pop answer block once modal has been closed
            backButtonLabel={formatMessage(threadFooterMessages.backButtonLabel)}
            onShowImmediate={startOpenEditorStartFocusWorkaround}
            isSubHeaderModal
            shouldFitContentHeight
          >
            {openAnswerBtn}
          </Modal>
        )}
      </div>
    </div>
  );
});
